export interface BootOptions {
  siteId: number;
  groupId?: number;
  clickToCall?: boolean;
  engagementId?: string;
  customVars?: any;
  visitorId?: string;
  isPopout?: boolean;
  chatRequestedFromUrl?: string;
  disableInline?: boolean;
  disableLauncher?: boolean;
  disableVisitorMonitoring?: boolean;
  cookieDomain?: string;
  skipPrechat?: boolean;
  isLGCanada?: boolean;
  privacyConsent?(done: (consentGiven: boolean) => void): void;
}

export interface VisitorCreateRequest {
  VisitorID?: string;
  VisitorName: string;
  VisitorEmail: string;
  Actions: VisitorAction[];
  CurrentUrl: string;
  DynamicProperties: any;
  FirstVisit: string;
  GroupID: number;
  HasCookieSupport: boolean;
  HasVisitedToday: boolean;
  LastAgentChattedWith: string;
  Referrer: string;
  ScreenSize: string;
  TimezoneOffset: string;
  VisitCount: string;
  PreRouteResult?: {
    GroupChanged: boolean;
    OldGroupId?: number;
    NewGroupId?: number;
    ChangeSyntax: string;
  };
  IgnoreInvites: boolean;
  IgnoreGroupSelection: boolean;
  HubspotId: string;
  FromPopout: boolean;
  EngagementId: string;
  UtmCodes: any;
  FrameVersion: 2;
}

export interface VisitorCreateResult {
  ReferredBy: string;
  Platform: string;
  Longitude: number;
  Location: string;
  Latitude: number;
  Language: string;
  Keywords: string;
  IntegrationConnections: string;
  IPAddress: string;
  HeadshotUrl: string;
  EmailAddress: string;
  PhoneNumber: string;
  DomainName: string;
  Country: string;
  CountryCode: string;
  MostRecentPageHit: string;
  AreaCode: number;
  VisitorID?: string;
  GroupID: number;
  DisplayName: string;
  City: string;
  ISP: string;
  Region: string;
  PostalCode: string;
  CurrentPage: string;
  FirstVisit: string;
  VisitCount: string;
  BrowserName: string;
  BrowserVersion: string;
  EndOfDay: Date;
  Invites: Array<VisitorInvite>;
  PreRouteResult?: {
    GroupChanged: boolean;
    OldGroupId?: number;
    NewGroupId?: number;
    ChangeSyntax: string;
  };
  GoogleAnalyticsId: string;
  HubspotId: string;
  Success: boolean;
  ErrorMessage: string;
}

export interface VisitorAction {
  ActionType: VisitorActionType;
  Value: string;
  When: Date;
}

export enum VisitorActionType {
  PageHit = 1,
  EngagementRequest = 2
}

export interface VisitorPageHit {
  url: string;
  viewedAt: Date;
}

export interface VisitorEngagements {
  [engagementId: string]: {
    agentName: string;
    startedAt: Date;
  };
}

export interface VisitorInvite {
  InviteId: number;
  TimeOnPageRule: string;
}

export interface ReferralResult {
  ShowReferral: boolean;
  TimeOnPageRule: string;
}

export interface Referral {
  ExternalUrl: string;
  Html: string;
}

export interface SurveyQuestion {
  UseCountryCode: boolean;
  MaskVisitorPhone: boolean;
  DisplayWidth: number;
  HiddenField: boolean;
  Html: string;
  KBSearchQuestion: boolean;
  PlaceholderText: string;
  Priority: number;
  QuestionID: number;
  QuestionText: string;
  QuestionType: QuestionType;
  Required: boolean;
  SpecifiedGroupIds: Array<number>;
  SpecifiedGroupLabelText: Array<string>;
  SurveyID: number;
  SurveyQuestionChoices: Array<string>;
  ValidationFailedMessage: string;
  InvalidEmailMessage: string;
  VariableName: string;
  DefaultSelectText: string;
}

export interface SurveyContent {
  Survey: Survey;
  HeaderText: string;
  ButtonText: string;
  ConfirmationText: string;
}

export interface Survey {
  DateCreated: string;
  LastModified: string;
  SiteID: number;
  SurveyID: number;
  SurveyQuestions: Array<SurveyQuestion>;
}

export enum QuestionType {
  Text = 1,
  Radio,
  Checkbox,
  Select,
  Name,
  Email,
  Group,
  Social,
  NPS,
  GroupRadio,
  HTML,
  Language,
  StarRating,
  TextArea,
  Tags,
  MultiSelect,
  PageBreak,
  Section,
  VisitorPhone,
  ClickToCall
}

export interface SurveyAnswer {
  QuestionID: number;
  Question: string;
  QuestionType: QuestionType;
  Answer: string;
  KBSearchQuestion: boolean;
  Priority: number;
}

export enum SurveyType {
  Prechat = 1,
  Postchat,
  Unavailable,
  AgentPostChat
}

export interface VisitorInfo {
  VisitorID: string;
  Country: string;
  CountryCode: string;
  AreaCode: number;
  IntegrationConnections: string;
  DomainName: string;
  DisplayName: string;
  MostRecentPageHit: string;
  City: string;
  EmailAddress: string;
  FirstVisit: Date | null;
  HeadshotUrl: string;
  IPAddress: string;
  ISP: string;
  Keywords: string;
  Language: string;
  Latitude: number | null;
  Longitude: number | null;
  Location: string;
  PostalCode: string;
  ReferredBy: string;
  Platform: string;
  BrowserName: string;
  BrowserVersion: string;
  CustomVars: { [name: string]: string };
  Actions: any[];
  UtmCodes: any;
  IsMobile: boolean;
}

export interface EngagementRequest {
  SiteID: number;
  GroupID: number;
  VisitorID: string;
  Name: string;
  Email: string;
  EngagementID: string;
  IsPolling: boolean;
  InviteConversionID: string;
  RequestedFrom: string;
  GroupAvailabilityStatus: number;
  PreEngagementId: string;
  IntroMessage?: string;
  CustomData: any;
  VisitorInfo: VisitorInfo;
  PreEngagementMessages: PreEngagementMessages;
  SurveyResults?: {
    GroupID: number;
    SiteID: number;
    VisitorID: string;
    EngagementID: string;
    SurveyID: number;
    SurveyType: SurveyType;
    Answers: Array<SurveyAnswer>;
  };
  SkippedPrechat?: boolean;
  VisitorLanguageDisplayName: string;
}

export interface PreEngagementMessages {
  AuthorName: string;
  AuthorId: number;
  AuthorType: "agent" | "bot";
  Messages: string[];
}

export interface PreEngagementRequest {
  SiteID: number;
  GroupID: number;
  GroupAvailabilityStatus: number;
  ChatRequestedFromPage: string;
  VisitorInfo: VisitorInfo;
}

export interface CallRequest {
  FromEngagementId: string;
  VisitorInfo: VisitorInfo;
  SiteID: number;
  GroupID: number;
  VisitorID: string;
  Name: string;
  Email: string;
  IsPolling: boolean;
  InviteConversionID: string;
  RequestedFrom: string;
  GroupAvailabilityStatus: number;
  IntroMessage?: string;
  CustomData: any;
  CallId: string;
  DirectPhoneNumber: string;

  SurveyResults?: {
    GroupID: number;
    SiteID: number;
    VisitorID: string;
    EngagementID: string;
    SurveyID: number;
    SurveyType: SurveyType;
    Answers: Array<SurveyAnswer>;
    ClickToCall: boolean;
  };
}

export enum EngagementContentType {
  PrechatSurvey = 1,
  FakeChat,
  UnavailableSurvey,
  UnavailableText,
  UnavailableUrl,
  Assigned,
  Queued,
  PostChatSurvey,
  KBSearch,
  BotChat
}

export interface EngagementContent {
  ContentType: EngagementContentType;
  EngagementID: string;
  GroupID: number;
  VisitorDisplayName: string;
  VisitorEmail: string;
  VisitorID: string;
  Content: any;
  BotState: BotState;
  VisitorLanguageCode: string;
  VisitorLanguageDisplayName: string;
  CustomData: Record<string, any>;
  CobrowseSessionId?: string;
  CobrowseState?: string;
  CobrowseRootUrl: string;
}

export interface SurveyResponse {
  GroupID: number;
  DateCreated: Date;
  EngagementID: string;
  CallId: string;
  SurveyID: number;
  SurveyType: number;
  Question: string;
  QuestionID: number;
  Answer: string;
  VisitorID: string;
  Choices: string;
  Priority: number;
}

export interface Call {
  CallId: string;
  CallStart: Date;
  Answered: Date;
  QueueSeconds: number;
  InProgressSeconds: number;
  VisitorId: string;
  VisitorName: string;
  VisitorPhone: string;
  AgentId: number;
  AgentDisplayName: string;
  AgentRole: string;
  AgentHeadshotUrl: string;
  SiteId: number;
  GroupId: number;
  SurveyResponses: Array<SurveyResponse>;
}

export interface CallContent {
  IsUnavailable: boolean;
  WasDispatched: boolean;
  Result: any;
  Call: Call;
}

export enum RealtimeState {
  Disconnected,
  Connected
}

export enum LineCreator {
  Agent = 1,
  System,
  Notice,
  Visitor,
  Bot
}

export enum LineType {
  Text = 1,
  Premade = 2,
  ModifiedPremade = 3,
  PushPage = 4,
  ClosingMessage = 5,
  WelcomeMessage = 6,
  ReroutingMessage = 7,
  PreEngagement = 8,
  ActionCard = 9,
  HTML = 10,
  QnaResults = 11
}

export enum EventTrackingType {
  TestEvent,
  VisitorClickedInline,
  VisitorClickedPopoutButton,
  VisitorShownPrechatSurvey,
  VisitorShownPostchatSurvey,
  VisitorShownMissedchatSurvey,
  VisitorSubmittedPrechatSurvey,
  VisitorSubmittedPostchatSurvey,
  VisitorSubmittedMissedchatSurvey,
  VisitorEnteredQueue,
  VisitorExitedQueue,
  VisitorEnteredChat,
  VisitorSentChatLine,
  VisitorExitedChat,
  ChatWasTransferred,
  AgentExitedChat,
  AgentLoggedIn,
  AgentLoggedOut,
  AgentChangedAvailabilityToAvailable,
  AgentChangedAvailabilityToUnavailable,
  AgentSurveySubmitted
}

export enum LineVisibility {
  Agent = 1,
  Manager = 2,
  All = 3
}

export enum LineRating {
  Unset = 0,
  Negative = 1,
  Positive = 2
}

export enum UserLevel {
  User,
  System
}

export interface ChatLine {
  DateCreated: string;
  EngagementID: string;
  EngagementUserID: string;
  FromDisplay: string;
  HeadshotUrl: string;
  ID: number;
  IsSystemSignOutMessage: boolean;
  LineCreator: LineCreator;
  LineType: LineType;
  LineVisibility: LineVisibility;
  MotionAIModule: string;
  OwnerLevel: UserLevel;
  Metadata: string;
  Rating: LineRating;
  RetryLineID?: number;
  Text: string;
  TextLanguage: string;
  UserID: number;
}

export interface Message {
  id: number;
  type: LineType;
  timestamp: Date;
  text: string;
  lineCreator: LineCreator;
  rating: LineRating;
  headshotUrl: string;
  author: string;
  status: MessageStatusType;
  metadata?: any;
}

export interface ActionCardData {
  ImageUrl: string;
  Title: string;
  Message: string;
  Actions: Action[];
}

export interface Action {
  Name: string;
  Label: string;
  Data: string;
}

export interface ActionPopoverContent {
  type: string;
  data: any;
}

export interface QuickReplyButton {
  label: string;
  pathId: string;
  sentiment?: string;
}

export enum MessageStatusType {
  Sent = "Sent",
  Received = "Received",
  Failure = "Failure",
  Resending = "Resending",
  Sending = "Sending"
}

export interface EngagementSettings {
  CompanyLogo: string;
  EnablePrechatSurvey: boolean;
  BotEnabled: boolean;
  PrioritizeAgentsOverBots: boolean;
  MaxUploadFileSize: number;
  FileUploadSizeErrorMessage: string;
  PrechatSurvey: Survey;
  UnavailableSurvey: Survey;
  PrechatHeaderText: string;
  PrechatSubmitText: string;
  OutOfScheduleSurveyText: string;
  OutOfScheduleCustomText: string;
  OutOfScheduleCustomHtml: string;
  AgentsBusySurveyText: string;
  AgentsBusyCustomText: string;
  AgentsBusyCustomHtml: string;
  UnavailableSurveyText: string;
  UnavailableCustomText: string;
  UnavailableCustomHtml: string;
  UnavailableSubmitText: string;
  UnavailableSurveySubmissionConfirmationText: string;
  UnavailableRedirectUrl: string;
  UnavailableAction: UnavailableAction;
  EnableVisitorAvailabilityPing: boolean;
  HideWhenUnavailable: boolean;
  ManualInvitesEnabled: boolean;
  HasInvites: boolean;
  HasEventTracking: boolean;
}

export enum GroupAvailabilityStatus {
  AgentsOffline,
  AgentsUnavailable,
  AgentsBusy,
  OutOfSchedule,
  Available
}

export enum UnavailableAction {
  Survey = 1,
  JustText,
  RedirectPage,
  Html
}

export enum ChatUIStage {
  Loading,
  LandingWidgets,
  PreChatSurvey,
  IntroForm,
  Queued,
  Assigned,
  AssignedToBot,
  UnavailableHtml,
  UnavailableSurvey,
  UnavailableUrl,
  PostChatSurvey,
  PostChatSurveySubmitted,
  UnavailableSurveySubmitted,
  KBResults,
  CallWaiting,
  Call,
  Referral,
  CloseWindow
}

export interface SubmitSurveyRequest {
  GroupID: number;
  SiteID: number;
  VisitorID: string;
  EngagementID: string;
  CallID?: string;
  SurveyID: number;
  SurveyType: SurveyType;
  Answers: Array<SurveyAnswer>;
  GroupAvailabilityStatus: GroupAvailabilityStatus;
}

export interface SocialData {
  source: string;
  displayName: string;
  email: string;
  headshotUrl: string;
}

export enum InvitationType {
  Inline = 1,
  Popover,
  Bubble
}

export enum InlineInviteContentType {
  Chat = 1,
  Image
}

export enum PopupPositionType {
  TopLeft = 0,
  TopCenter,
  TopRight,
  CenterLeft,
  Center,
  CenterRight,
  BottomLeft,
  BottomCenter,
  BottomRight
}

export interface InviteDetails {
  author: InviteAuthor;
  invite: {
    AllowDuringChat: boolean;
    AnchorTo: string;
    Contents: string;
    CreateManually: boolean;
    Description: string;
    EndLocationX: number;
    EndLocationY: number;
    EndingAnimationPosition: PopupPositionType;
    Expression: string;
    FloatSpeed: number;
    GalleryItemID: number;
    Height: number;
    InlineChatMessage: string;
    InlinePlaceholderText: string;
    InlineContentType: InlineInviteContentType;
    InvitationID: number;
    InvitationType: InvitationType;
    IsDeleted: boolean;
    MaxRefires: number;
    Name: string;
    PopUpPosition: PopupPositionType;
    Priority: number;
    RedirectToGroup: number;
    SecondsToDisplay: number;
    ShouldFloat: boolean;
    SiteID: number;
    StartLocationX: number;
    StartLocationY: number;
    StartingAnimationPosition: PopupPositionType;
    TransparentBackground: boolean;
    UniqueGalleryItemID: string;
    Width: number;
    MessagesBlob: string;
  };
}

export interface InviteAuthor {
  Name?: string;
  Id?: number;
  HeadshotUrl?: string;
  Type: "bot" | "agent";
}

export interface PopoverInvite {
  html: string;
  css: string;
  animationStartPosition: PopupPositionType;
  animationStopPosition: PopupPositionType;
  secondsToDisplay?: number;
  expression: string;
}

export interface InlineChatInvite {
  authorName: string;
  authorId: number;
  authorHeadshotUrl: string;
  authorType: "bot" | "agent";
  messages: string[];
  type: InvitationType;
  contentType: InlineInviteContentType;
  placeholderText: string;
  //used to indicate that this chat invite came from the bubble invite
  fromBubble: boolean;
  expression: string;
}

export interface InlineImageInvite {
  imageUrl: string;
  secondsToDisplay?: number;
  expression: string;
}

export interface KBArticle {
  name: string;
  url: string;
}

export interface VisitorText {
  SiteId: number;
  VisitorId: string;
  EngagementId: string;
  Text: string;
}

export interface CobrowsingRequest {
  EngagementId: string;
  VisitorId: string;
  Link?: string;
}

export interface VisitorBotMessage {
  VisitorText: VisitorText;
  Type: VisitorBotMessageType;
  NextPath: string;
  Sentiment: string;
}

export interface VisitorBotMessageResult {
  ErrorCode: string;
  BotState: BotState;
  Result: string;
  Responses: ChatLine[];
  VisitorLine: ChatLine;
  VisitorName: string;
  VisitorEmail: string;
  VisitorPhone: string;
  CustomData: Record<string, any>;
}

export interface RouteToBotFollowupResponse {
  EngagementContext: EngagementContent;
  NextPath: string;
}

export interface BotState {
  EnableTextReplies: boolean;
  Status: string;
  QuickReplies: QuickReplyButton[];
  DataField: DataField;
  Form: Form;
  KnowledgeBaseSearchResults: KnowledgeBaseSearchResults;
  SearchSpringResults: SearchSpringResults;
  CalendlyResult: CalendlyResult;
  EnableAudio: boolean;
  EnableTypingIndicator: boolean;
  EnableTimestamps: boolean;
  MessageSpeed: number;
}

export interface DataField {
  name: string;
  format:
    | "Text"
    | "Number"
    | "Email"
    | "MultilineText"
    | "Buttons"
    | "Dropdown";
  options: string[];
}

export interface Form {
  fields: FormField[];
}

export interface FormField {
  label: string;
  placeholder: string;
  dataField: DataField;
}

export interface BotFollowupModel {
  EngagementId: string;
}

export interface WebhookResult {
  NextPathId: string;
  Lines: ChatLine[];
}

export interface SecureForm {
  FormUrl: string;
  SentAt: Date;
  State: "Sent" | "Submitted" | "Waiting";
}

export enum VisitorBotMessageType {
  Start,
  Progress,
  BookedMeeting,
  End
}

export interface EngagementUpdate {
  EngagementId: string;
  VisitorId: string;
}

export interface ExitVisitorUpdate {
  EngagementId: string;
  VisitorId: string;
  GroupId: number;
  WasChatting: boolean;
}

export interface RatingUpdate {
  Rating: number;
  EngagementId: string;
  VisitorId: string;
}

export interface LineRatingUpdate {
  Id: number;
  Rating: LineRating;
  EngagementId: string;
  VisitorId: string;
}

export interface EngagementConfiguration {
  CallsEnabled: boolean;
  CallButtonNumber: string;
  CallButtonUrl: string;
  SecureFormRequestText: string;
  PrimaryBackgroundColor: string;
  PrimaryTextColor: string;
  BorderRadius: number;
  HeaderBackgroundImage: string;
  HeaderBackgroundUrl: string;
  HeaderTransparency: number;
  ForceMinimizedHeader: boolean;
  UseMinimizedHeaderDesktop: boolean;
  UseMinimizedHeaderMobile: boolean;
  ExpandChatWindowMobile: boolean;
  IntroductionText: string;
  QueuePositionText: string;
  QueueEstimateEnabled: boolean;
  QueueEstimateText: string;
  QueueHeaderText: string;
  QueueTimerEnabled: boolean;
  ChatRatingText: string;
  HeaderLogoUrl: string;
  HeaderTitle: string;
  FrameWidth: number;
  FrameHeight: number;
  Font: string;
  UploadEnabled: boolean;
  EmailEnabled: boolean;
  ChatRatingEnabled: boolean;
  ChatLineRatingEnabled: boolean;
  PrintEnabled: boolean;
  EyecatcherOnlineUrl: string;
  EyecatcherOnlineX: number;
  EyecatcherOnlineY: number;
  EyecatcherOnlineScale: number;
  EyecatcherOfflineUrl: string;
  EyecatcherOfflineX: number;
  EyecatcherOfflineY: number;
  EyecatcherOfflineScale: number;
  ChatHeaderImageScale: number;
  SystemTextColor: string;
  ChatHeaderImageType: "AgentHeadshot" | "CompanyLogo" | "None";
  ChatLineImageType: "AgentHeadshot" | "CompanyLogo";
  ChatPlaceholder: string;
  IntroPlaceholder: string;
  AvailableLauncherCustomText: string;
  AvailableLauncherFontSize: number;
  AvailableLauncherBorderRadius: number;
  AvailableLauncherCustomImageUrl: string;
  AvailableLauncherBackgroundColor: string;
  AvailableLauncherTextColor: string;
  AvailableLauncherIconHeight: number;
  AvailableLauncherIconWidth: number;
  AvailableLauncherX: number;
  AvailableLauncherY: number;
  AvailableLauncherPadding: number;
  UnavailableLauncherCustomText: string;
  UnavailableLauncherFontSize: number;
  UnavailableLauncherBorderRadius: number;
  UnavailableLauncherCustomImageUrl: string;
  UnavailableLauncherBackgroundColor: string;
  UnavailableLauncherTextColor: string;
  UnavailableLauncherIconHeight: number;
  UnavailableLauncherIconWidth: number;
  UnavailableLauncherX: number;
  UnavailableLauncherY: number;
  UnavailableLauncherPadding: number;
  ChattingLauncherType: "AgentHeadshot" | "CustomImage";
  ChattingLauncherCustomImageUrl: string;
  ChattingLauncherX: number;
  ChattingLauncherY: number;
  FacebookButtonEnabled: boolean;
  TwitterButtonEnabled: boolean;
  GoogleButtonEnabled: boolean;
  LinkedInButtonEnabled: boolean;
  FrameBackgroundColor: string;
  FrameTextColor: string;
  VisitorTypeaheadEnabled: boolean;
  AdvancedEcommerceIntegrations: boolean;
  PopoutButtonEnabled: boolean;
  NewMessageSound: string;
  TwitterUrl: string;
  FacebookUrl: string;
  GoogleUrl: string;
  LinkedInUrl: string;
  ShowReferral: boolean;
  ShowReferralInFrame: boolean;
  ShowReferralConditionally: boolean;
  ShowUnreadMessageCountInTitle: boolean;
  ReferralWindowWidth?: number;
  ReferralWindowHeight?: number;
  TypingIndicatorText: string;
  LandingWidgetsConfig: LandingWidgetsConfig;
  ButtonRadius: number;
  PrimaryButtonColor: string;
  PrimaryButtonTextColor: string;
  PrimaryButtonBorderWidth: number;
  PrimaryButtonBorderColor: string;
  SecondaryButtonColor: string;
  SecondaryButtonTextColor: string;
  SecondaryButtonBorderWidth: number;
  SecondaryButtonBorderColor: string;
  DangerButtonColor: string;
  DangerButtonTextColor: string;
  DangerButtonBorderWidth: number;
  DangerButtonBorderColor: string;
  ReplyButtonColor: string;
  ReplyButtonTextColor: string;
  ReplyButtonBorderWidth: number;
  ReplyButtonBorderColor: string;
  YesButtonText: string;
  NoButtonText: string;
  CloseChatConfirmationText: string;
  CloseChatButtonText: string;
  CloseChatHeaderText: string;
  TodayAtText: string;
  YesterdayAtText: string;
  MenuPrintText: string;
  MenuEmailTranscriptText: string;
  MenuPopoutText: string;
  SurveyNextPageButtonText: string;
  SurveyPreviousPageButtonText: string;
  EmailModalTitleText: string;
  EmailModalInputLabelText: string;
  EmailModalCancelButtonText: string;
  EmailModalSubmitButtonText: string;
  EmailModalValidationText: string;
  Enable24HourTimestamps: string;
  CustomFooterEnabled: boolean;
  CustomFooterText: string;
  CustomFooterUrl: string;
  PopoutDocumentTitle?: string;
  EnablePoweredBy: boolean;
  AgentChatBubbleBackgroundColor: string;
  AgentChatBubbleTextColor: string;
  AgentChatBubbleBorderColor: string;
  VisitorChatBubbleBackgroundColor: string;
  VisitorChatBubbleTextColor: string;
  VisitorChatBubbleBorderColor: string;
  Language: string;
}

export interface RelayRequest {
  Url: string;
  BasicAuthHeader: string;
  Data: any;
}

export interface LandingWidgetsConfig {
  Enabled: boolean;
  WidgetPrimaryColor: string;
  StartChatWidget: StartChatWidgetConfig;
  NetsuiteOrderLookupWidget: NetsuiteOrderLookupWidget;
  KBWidget: KBWidgetConfig;
  CallWidget: CallWidgetConfig;
  FormWidgets: FormWidgetConfig[];
}

export interface CallWidgetConfig {
  Enabled: boolean;
  LabelText: string;
  SideImageUrl: string;
  SideImageGradientEnabled: boolean;
  InputLabel: string;
  ButtonLabel: string;
  UseCountryCode: boolean;
  DefaultCountryCode: string;
  MaskVisitorPhone: boolean;
  ShowExternalPhoneNumber: boolean;
  ExternalPhoneNumberText: string;
  ExternalPhoneNumber: string;
}

export interface StartChatWidgetConfig {
  Enabled: boolean;
  LabelText: string;
  ButtonText: string;
  SideImageUrl: string;
  SideImageGradientEnabled: boolean;
  StartChatButtonLabel: string;
}

export interface NetsuiteOrderLookupWidget {
  Enabled: boolean;
  EmailLabelText: string;
  ZipCodeLabelText: string;
  LookupButtonText: string;
  OrderNumberLabelText: string;
  LabelText: string;
  SubLabelText: string;
  InvalidEntryErrorText: string;
  NoResultsErrorMessage: string;
  AmazonResultErrorMessage: string;
}

export interface NetsuiteSalesOrder {
  linkedtrackingnumbers: string;
  shipdate: string | undefined;
  shipcarrier: string;
  id: string;
  values: NetsuiteSalesOrderValue;
}

export interface NetsuiteSalesOrderValue {
  datecreated: Date;
  tranid: string;
}

export interface StartChatWidgetConfig {
  Enabled: boolean;
  LabelText: string;
  ButtonText: string;
  SideImageUrl: string;
  SideImageGradientEnabled: boolean;
  StartChatButtonLabel: string;
}

export interface FormWidgetConfig {
  Enabled: boolean;
  LabelText: string;
  SideImageUrl: string;
  SideImageGradientEnabled: boolean;
  WidgetName: string;
  InputLabel: string;
  ButtonLabel: string;
  EndpointUrl: string;
  EndpointAuth: string;
}

export interface KBWidgetConfig {
  Enabled: boolean;
  LabelText: string;
  SideImageUrl: string;
  SideImageGradientEnabled: boolean;
  SearchTermInputLabel: string;
  SearchButtonLabel: string;
}

export interface DynamicButtonConfiguration {
  DynamicButtonOnlineUrl: string;
  DynamicButtonOfflineUrl: string;
}

export interface CallButtonConfiguration {
  CallButtonUrl: string;
  CallButtonNumber: string;
}

export interface EndpointModel {
  EngagementApiUrl: string;
  EngagementSignalRUrl: string;
  VisitorApiUrl: string;
  AzureStorageEndpoint: string;
  ConsoleUrl: string;
}

export interface EcommerceCartItem {
  Name: string;
  Price: number;
  Quantity: number;
  Thumbnail: string;
  Url: string;
}

export interface EcommerceProduct {
  Name: string;
  Thumbnail: string;
  Url: string;
}

export interface EcommerceData {
  CartTotal: number;
  CartItems: Array<EcommerceCartItem>;
  RecentlyViewed: Array<EcommerceProduct>;
}

export interface ChatContextProps {
  chatStage: ChatUIStage;
  setChatStage: React.Dispatch<React.SetStateAction<ChatUIStage>>;
  closedByAgent: boolean;
  setClosedByAgent: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface EndpointContextProps {
  images: string;
}

export interface AddConversionModel {
  ConversionID: number;
  GroupID: number;
  VisitorID: string;
  DollarAmount: number;
  CustomData: string;
}

export interface ExpressionRequestModel {
  GroupId?: number;
  DisplayName: string;
  PageHitCount: number;
  WasInviteFired: boolean;
  HasChatted: boolean;
  City: string;
  ISP: string;
  Region: string;
  PostalCode: string;
  Host: string;
  Referrer: string;
  CurrentUrl: string;
  Country: string;
  CustomVars: Record<string, string>;
  InviteViews: Record<number, number>;
  PreviousPages: string[];
  VisitorId: string;
  UtmCodes: any;
}

export interface GroupSelectionRequestModel {
  VisitorId: string;
  GroupId?: number;
  DisplayName: string;
  PageHitCount: number;
  WasInviteFired: boolean;
  HasChatted: boolean;
  City: string;
  ISP: string;
  Region: string;
  PostalCode: string;
  Host: string;
  Referrer: string;
  CurrentUrl: string;
  Country: string;
  CustomVars: Record<string, string>;
  InviteViews: Record<number, number>;
  PreviousPages: string[];
  UtmCodes: any;
}

export interface GroupSelectionResult {
  GroupChanged: boolean;
  OldGroupId?: number;
  NewGroupId?: number;
  ChangeSyntax: string;
}

export interface KnowledgeBaseSearchResults {
  HelpfulButtonLabel: string;
  HelpfulPathId: string;
  NotHelpfulButtonLabel: string;
  NotHelpfulPathId: string;
  Articles: KnowledgeBaseArticle[];
}

export interface KnowledgeBaseArticle {
  Id: string;
  Title: string;
  Description: string;
  Url: string;
}

export interface SearchSpringResults {
  Pagination: any;
  Results: SearchSpringResult[];
}

export interface SearchSpringResult {
  Name: string;
  Brand: string;
  ImageUrl: string;
  Price: number;
}

export interface CalendlyResult {
  CalendarName: string;
  BookedPathId: string;
  SkippedPathId: string;
}

export interface QnaResult {
  Answers: QnaAnswer[];
}

export interface QnaAnswer {
  Question: string;
  Answer: string;
}

export interface VisitorLogRequest {
  Event: VisitorEvent; //The event being logged
  Events: VisitorEvent[]; //Past events recorded on this visitor
  UI: "new";
  PageGroupId: number;
  DomainScope: string;
  IsPopout: boolean;
  InlineEnabled: boolean;
}

export enum LogLevel {
  info = "info",
  debug = "debug",
  error = "error"
}

export interface VisitorEvent {
  Date: Date;
  Message: string;
  Level: LogLevel;
  VisitorId: string;
  EngagementId: string;
  PreEngagementId: string;
  ActiveGroupId: number;
}
